import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, AlertTriangle, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { safeText } from "@/lib/safeText";
import { useAuth } from "@/context/AuthContext";
import { TooltipButton } from "@/components/common/Tooltips";
import { RiesgoEBRBreakdown } from "@/components/riesgo/RiesgoEBRBreakdown";
import { GRADO_TONE as RIESGO_TONE } from "@/lib/riesgoTokens";
import { CoincidenciasProspecto } from "@/components/screening/FichaCoincidencia";

function Badge({ children, tone }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone}`}>
      {children}
    </span>
  );
}

function ListasCotejadas({ listas, cotejoExterno }) {
  // oct-2026 · El resultado enumera TODAS las listas cotejadas con su versión (antes solo
  // decía "Cotejado contra N lista(s)" con los primeros 6 nombres truncados) y, al final,
  // el estado real del cotejo externo.
  if (!listas.length) {
    return (
      <p className="text-[11px] text-slate-500" data-testid="riesgo-listas-vacias">
        Sin listas cruzadas registradas en esta corrida.
      </p>
    );
  }
  const externoNo = cotejoExterno && cotejoExterno.ejecutado === false;
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5" data-testid="riesgo-listas-cotejadas">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        Listas cotejadas ({listas.length})
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {listas.map((l, i) => {
          const noRealizada = /NO realizado/i.test(String(l));
          return (
            <li key={`lst-${i}`} data-testid={`riesgo-lista-${i}`}
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${
                  noRealizada
                    ? "border-amber-300 bg-amber-50 font-semibold text-amber-800"
                    : "border-slate-200 bg-slate-50 text-slate-700"
                }`}>
              {l}
            </li>
          );
        })}
      </ul>
      {externoNo && (
        <p className="mt-1.5 text-[11px] text-amber-800" data-testid="riesgo-externo-no-realizado">
          El cotejo externo NO se ejecutó: {cotejoExterno.motivo || "sin motivo"}. Las listas de
          arriba son las que sí se cotejaron.
        </p>
      )}
    </div>
  );
}

export function ProspectoRiesgoPanel({ prospecto, onDone }) {
  const { user } = useAuth();
  const res = prospecto?.screening_resultado || {};
  const lpbCoincidencia = res?.lpb?.coincidencia || prospecto?.lista_bloqueados === "Coincidencia";
  const pep = res?.pep || {};
  const grado = prospecto?.grado_riesgo || prospecto?.nivel_riesgo || "";
  const origen = prospecto?.riesgo_origen;
  const evaluado = !!prospecto?.screening_evaluado;
  // feb-2026 (P0-B) — Cuando el cotejo externo no completó, NO afirmar "Sin coincidencia"
  // verde para LPB/PEP. Se sustituye por un único aviso ámbar con el motivo.
  const pendienteReal = !!prospecto?.screening_pendiente;
  const motivoPend = String(prospecto?.screening_pendiente_motivo || "").split(":", 1)[0] || "sin_motivo";
  // feb-2026 (audit) — El resumen de arriba estaba pintando VERDE aunque el motor EBR
  // arrojara "Coincidencia potencial en lista crítica" (OFAC/ONU/SAT69/SAT69B, etc.).
  // Fuente única de verdad: `screening_resultado.criticas` (persistido en screening.py:1717)
  // + `screening_resultado.listas_cruzadas`. Ahora el consolidado refleja TODAS las
  // listas cotejadas y sube a rojo ante cualquier crítica (no solo LPB/PEP).
  const criticasArr = Array.isArray(res?.criticas) ? res.criticas.filter(Boolean) : [];
  const listasCruzadas = Array.isArray(res?.listas_cruzadas) ? res.listas_cruzadas : [];
  // oct-2026 · sello honesto del cotejo externo (cotejo_externo_estado.py). `ejecutado:false`
  // significa simulación, entorno indeterminado, proveedor inactivo, error o sin respuesta.
  const cotejoExterno = res?.cotejo_externo || null;
  const hayCritica = criticasArr.length > 0 || lpbCoincidencia;
  const hayPep = !!pep.detectado;
  const limpioReal = evaluado && !pendienteReal && !hayCritica && !hayPep;

  const esOC = user?.role === "oficial_cumplimiento";
  const [open, setOpen] = useState(false);
  const [nivel, setNivel] = useState(grado || "");
  const [justificacion, setJustificacion] = useState("");
  const [saving, setSaving] = useState(false);

  const guardarAjuste = async () => {
    setSaving(true);
    try {
      await api.post("/riesgo/cedula", {
        prospecto_id: prospecto.id, nivel, justificacion,
      });
      toast.success("Ajuste excepcional registrado y sellado en bitácora");
      setOpen(false);
      setJustificacion("");
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="prospecto-riesgo-panel" className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <div className="mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Resultado de screening PLD (automático)</p>
      </div>

      {!evaluado ? (
        <p className="text-sm text-slate-500">El cotejo automático se ejecuta al guardar el prospecto. Aún sin resultado.</p>
      ) : pendienteReal && !hayCritica && !hayPep ? (
        // P0-B: el cotejo NO terminó bien. No decir "Sin coincidencia" verde de LPB/PEP.
        <div className="space-y-3 text-sm">
          <div data-testid="riesgo-sin-verificar"
               className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold uppercase tracking-wide">Cotejo externo NO realizado — reintentar</p>
              <p className="mt-1">
                La consulta al proveedor de listas (cotejo externo) no se ejecutó en este alta.
                Motivo: <span className="font-mono">{motivoPend}</span>
                {cotejoExterno?.modo ? ` · modo del entorno: ${cotejoExterno.modo}` : ""}.
                El sistema lo reintenta automáticamente y el prospecto queda PENDIENTE de
                screening. NO se afirma limpieza de LPB ni PEP sobre datos que no se obtuvieron.
              </p>
            </div>
          </div>
          <ListasCotejadas listas={listasCruzadas} cotejoExterno={cotejoExterno} />
          {origen && (
            <p className="text-[11px] text-slate-500" data-testid="riesgo-origen">
              Origen del cálculo: {origen === "ajuste_oc" ? "Ajuste excepcional del OC" : origen === "automatico" ? "Cálculo automático (grado EBR local, sin bloqueo por listas)" : origen}
            </p>
          )}
          {safeText(prospecto?.riesgo_motivo) && <p className="text-xs text-slate-500">{safeText(prospecto.riesgo_motivo)}</p>}
          {res?.fecha && (
            <p className="text-[11px] text-slate-400">Cotejo del {res.fecha} por {res.usuario} · {listasCruzadas.length} lista(s) cruzada(s) — externa pendiente</p>
          )}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          {/* feb-2026 (audit) — Consolidado honesto: refleja TODAS las listas cotejadas
              (LPB nacional + OFAC + ONU + SAT 69/69-B + PEP + PGR/Adverse/etc. vía cotejo externo).
              Rojo ante cualquier crítica (score>=85). Ámbar si solo PEP. Verde SOLO cuando
              cotejo completo terminó sin ninguna coincidencia crítica ni PEP. */}
          <div data-testid="riesgo-consolidado"
               className={`flex items-start gap-2 rounded-md border-l-4 p-3 ${
                 hayCritica
                   ? "border-red-500 bg-red-50 text-red-800"
                   : hayPep
                     ? "border-amber-500 bg-amber-50 text-amber-800"
                     : "border-emerald-500 bg-emerald-50 text-emerald-800"
               }`}>
            {hayCritica ? <ShieldX className="mt-0.5 h-5 w-5 shrink-0" />
              : hayPep ? <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                : <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold" data-testid="riesgo-consolidado-titulo">
                {hayCritica
                  ? (lpbCoincidencia
                      ? `Coincidencia en Lista de Personas Bloqueadas (LPB)${criticasArr.length ? ` +${criticasArr.length} lista(s) crítica(s)` : ""}`
                      : `Coincidencia crítica detectada (${criticasArr.length} lista${criticasArr.length === 1 ? "" : "s"})`)
                  : hayPep
                    ? "Coincidencia PEP detectada"
                    : "Sin coincidencia en ninguna lista cotejada"}
              </p>
              <p className="mt-0.5 text-[11px] opacity-90">
                {listasCruzadas.length > 0
                  ? `Cotejado contra ${listasCruzadas.length} lista(s)`
                  : "Sin listas cruzadas registradas en esta corrida."}
              </p>
            </div>
          </div>

          <ListasCotejadas listas={listasCruzadas} cotejoExterno={cotejoExterno} />

          {/* Detalle de coincidencias críticas cuando existen (LPB + OFAC + ONU + SAT 69/69-B + Q-Detect críticas). */}
          {hayCritica && (
            <div data-testid="riesgo-criticas-detalle" className="rounded-md border border-red-200 bg-white p-2.5">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-700">
                Listas con coincidencia crítica
              </p>
              <ul className="space-y-1 text-xs">
                {lpbCoincidencia && res?.lpb?.detalle && (
                  <li key="lpb-detalle" data-testid="riesgo-critica-lpb" className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                      Lista de Personas Bloqueadas (LPB)
                    </span>
                    <span className="text-slate-600">score {res.lpb.detalle.score}</span>
                    <span className="text-slate-400">· v{res.lpb.detalle.version}</span>
                  </li>
                )}
                {criticasArr.map((c, i) => (
                  <li key={`crit-${i}`} data-testid={`riesgo-critica-${i}`} className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                      {c.lista}
                    </span>
                    <span className="text-slate-600">score {c.score}</span>
                    {c.tipo && <span className="text-slate-400">· {c.tipo}</span>}
                    {c.version && <span className="text-slate-400">· v{c.version}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Chip PEP declarado vs detectado (subordinado al consolidado, para trazabilidad). */}
          <div className="flex flex-wrap items-center gap-2" data-testid="riesgo-pep">
            <span className="text-slate-600">PEP — declarado:</span>
            <Badge tone="bg-slate-100 text-slate-700 border-slate-200">{pep.declarado ? "Sí" : "No"}</Badge>
            <span className="text-slate-600">cotejo:</span>
            <Badge tone={pep.detectado ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>
              {pep.detectado ? "Coincidencia PEP" : "Sin coincidencia"}
            </Badge>
          </div>
          {pep.discrepancia && (
            <div data-testid="riesgo-pep-discrepancia" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Discrepancia PEP: lo declarado no coincide con el cotejo. Se generó una alerta para el Oficial de Cumplimiento. Lo declarado no apaga el resultado del cotejo.</span>
            </div>
          )}

          {/* Aviso ÁMBAR cuando la parte externa (Q-Detect) quedó pendiente pero hay críticas locales.
              Evita interpretar el estado global como "verde por cotejo terminado". */}
          {pendienteReal && (
            <div data-testid="riesgo-pendiente-parcial" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Cotejo externo quedó pendiente. Motivo: <span className="font-mono">{motivoPend}</span>.
                Las coincidencias mostradas provienen del cotejo local; el resultado externo podría añadir listas al reintentarse.
              </span>
            </div>
          )}

          {/* Origen del cálculo del grado (etiqueta breve; el grado consolidado lo pinta el desglose) */}
          {origen && (
            <p className="text-[11px] text-slate-500" data-testid="riesgo-origen">
              Origen del cálculo: {origen === "ajuste_oc" ? "Ajuste excepcional del OC" : origen === "automatico" ? "Cálculo automático" : origen}
            </p>
          )}
          {safeText(prospecto?.riesgo_motivo) && <p className="text-xs text-slate-500">{safeText(prospecto.riesgo_motivo)}</p>}
          {res?.fecha && (
            <p className="text-[11px] text-slate-400">Cotejo del {res.fecha} por {res.usuario} · {listasCruzadas.length} lista(s) cruzada(s){limpioReal ? " · sin hallazgos" : ""}</p>
          )}
        </div>
      )}

      {prospecto?.id && (
        <div className="mt-3">
          <CoincidenciasProspecto prospectoId={prospecto.id} onDecided={onDone} />
        </div>
      )}

      {/* Desglose CONSOLIDADO del EBR (5 factores + presencia de dato). Fuente única:
          GET /api/riesgo/perfil-ebr/{prospecto_id}?tipo=prospecto. Se muestra SIEMPRE (aunque el
          screening automático no haya corrido aún, el motor EBR ya se ejecutó al alta). */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
          Grado EBR consolidado — los 5 factores
        </p>
        <RiesgoEBRBreakdown docId={prospecto.id} tipo="prospecto" canRecalcular={false} dense />
      </div>

      {esOC && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <Button data-testid="ajuste-oc-btn" variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
            <Gavel className="h-4 w-4" /> Ajuste excepcional justificado
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajuste excepcional del grado de riesgo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-xs text-slate-500">
              El cálculo automático (EBR) es el valor por defecto. Este ajuste queda sellado en la bitácora con tu nombre y la fecha.
            </p>
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Grado de riesgo</Label>
              <Select value={nivel} onValueChange={setNivel}>
                <SelectTrigger data-testid="ajuste-oc-nivel">
                  <SelectValue placeholder="Selecciona el grado..." />
                </SelectTrigger>
                <SelectContent>
                  {["Bajo", "Medio", "Alto"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Justificación (mínimo 10 caracteres)</Label>
              <Textarea data-testid="ajuste-oc-justificacion" rows={4} value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                placeholder="Motivo del ajuste excepcional al grado de riesgo derivado por el motor..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="ajuste-oc-guardar" onClick={guardarAjuste}
              disabled={saving || !nivel || justificacion.trim().length < 10}
              className="bg-primary hover:bg-sidebar-hover">
              {saving ? "Guardando..." : "Registrar ajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProspectoRiesgoButton({ prospecto, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TooltipButton tooltip="Ver el resultado del análisis PLD y riesgo del prospecto"
        data-testid={`riesgo-pld-${prospecto.id}`} variant="outline" size="sm"
        className="h-8 gap-1.5 px-2.5 text-primary" onClick={() => setOpen(true)}>
        <ShieldCheck className="h-4 w-4" /> Riesgo PLD
      </TooltipButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Riesgo PLD — {prospecto.nombre || prospecto.razon_social}</DialogTitle>
          </DialogHeader>
          <ProspectoRiesgoPanel prospecto={prospecto} onDone={() => { onDone?.(); }} />
        </DialogContent>
      </Dialog>
    </>
  );
}
